import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import { Autocomplete, Chip, Button } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useToast from "../../../helpers/useToast";
import useEncryption from "../../../services/useEncryption";
import { addDisplayDataApi } from "../../../api/userApi/userApi";
import { createAuditLogs } from "../../../api/providerApi/providerApi";
import { useSelector } from "react-redux";
import useHeaders from "../../../helpers/useHeaders";

const UpdateInsuranceModal = ({
  open,
  handleClose,
  insuranceDetails,
  userId,
  setUpdate,
  doctorData,
}) => {
  const { encryptData, decryptData } = useEncryption();
  const { id } = useParams();
  const toastOption = useToast();
  const decryptedId = decryptData(decodeURIComponent(id));
  const [loading, setLoading] = useOutletContext();
  const { role } = useSelector((state) => state.auth);
  const headers = useHeaders();

  const formik = useFormik({
    initialValues: {
      insurance_details: insuranceDetails || [],
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      insurance_details: Yup.array()
        .of(Yup.string().required())
        .min(1, "At least one insurance is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      const formData = new FormData();

      // Fill all required fields for the API, using doctorData for the rest
      formData.append("firstName", doctorData?.firstName || "");
      formData.append("lastName", doctorData?.lastName || "");
      formData.append("streetAddress", doctorData?.streetAddress || "");
      formData.append("city", doctorData?.city || "");
      formData.append("state", doctorData?.state || "");
      formData.append("country", doctorData?.country || "");
      formData.append("zipCode", doctorData?.zipCode || "");
      formData.append("aboutMe", doctorData?.aboutMe || "");
      formData.append("organizationId", userId);
      formData.append(
        "insurance_details",
        JSON.stringify(values.insurance_details)
      );
      if (doctorData?.photo) {
        formData.append("profileImg", doctorData.photo);
      }

      try {
        let response = await addDisplayDataApi(formData);

        setLoading(false);
        handleClose();
        resetForm();
        setUpdate((prev) => !prev);

        let obj = {
          event: `${role} has updated insurance details.`,
        };
        await createAuditLogs({ data: encryptData(obj) }, headers);

        toast.success(response.data.message, toastOption);
      } catch (error) {
        setLoading(false);
        if (error.response) {
          toast.error(
            error.response.data.message || "An error occurred.",
            toastOption
          );
        } else {
          toast.error("Network error. Please try again.", toastOption);
        }
      }
    },
  });

  return (
    <Dialog open={open} onClose={handleClose}>
      <div className="d-flex align-items-center justify-content-between">
        <DialogTitle>Edit In-network Insurances</DialogTitle>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <DialogContent dividers>
        <form onSubmit={formik.handleSubmit}>
          <Autocomplete
            multiple
            freeSolo
            options={[]} // No predefined options
            value={formik.values.insurance_details}
            onChange={(event, newValue) =>
              formik.setFieldValue("insurance_details", newValue)
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Add Insurance Types"
                placeholder="Type and press Enter"
                size="small"
                error={
                  formik.touched.insurance_details &&
                  Boolean(formik.errors.insurance_details)
                }
                helperText={
                  formik.touched.insurance_details &&
                  formik.errors.insurance_details
                }
              />
            )}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={formik.handleSubmit} color="primary" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateInsuranceModal;